import CryptoJS from "crypto-js";
import store from "@/store";
import { KokattoInboxServiceClient } from "@/serviceClients/KokattoInboxServiceClient";

export class ClientSessionKeyManager {
  private static instance: ClientSessionKeyManager;
  private dataKey: CryptoJS.lib.WordArray | null = null;
  private sessionKey: string | null = null;
  private inboxService: KokattoInboxServiceClient;
  private initPromise: Promise<void> | null = null;

  private constructor() {
    this.inboxService = new KokattoInboxServiceClient({
      token: store.getters.getKokattoTokenAccess,
    });
  }

  public static getInstance(): ClientSessionKeyManager {
    if (!ClientSessionKeyManager.instance) {
      ClientSessionKeyManager.instance = new ClientSessionKeyManager();
    }
    return ClientSessionKeyManager.instance;
  }

  public async initializeSession(forceNew = false): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    // If not forcing new and already have keys, just return
    if (!forceNew && this.isSessionInitialized()) {
      return;
    }

    this.initPromise = this._doInitialize(forceNew);
    return this.initPromise;
  }

  private async _doInitialize(forceNew: boolean): Promise<void> {
    try {
      console.log("Initializing hybrid encryption session...");
      const startTime = Date.now();

      // Check if already initialized from sessionStorage (unless forcing new)
      if (!forceNew && this.restoreFromStorage()) {
        console.log("Encryption session restored from storage");
        return;
      }

      // Get token - fail fast if not logged in
      const token = store.getters.getKokattoTokenAccess;
      if (!token) {
        throw new Error("No auth token available - login required");
      }

      // Step 1: Get KMS-encrypted session key from backend
      this.sessionKey = await this.inboxService.generateSessionKey();
      console.log("Session key received from backend");

      // Step 2: Generate AES-256 symmetric key for response encryption
      this.dataKey = CryptoJS.lib.WordArray.random(32); // 256-bit key
      console.log("Data key (AES-256) generated for response encryption");

      sessionStorage.setItem(
        "dataKey",
        this.dataKey.toString(CryptoJS.enc.Base64)
      );
      sessionStorage.setItem("sessionKey", this.sessionKey || "");

      const initTime = Date.now() - startTime;
      console.log(`Hybrid encryption session initialized in ${initTime}ms`);
    } catch (error) {
      console.error("Failed to initialize hybrid encryption session:", error);
      this.initPromise = null;
      throw error;
    }
  }

  private restoreFromStorage(): boolean {
    const storedDataKey = sessionStorage.getItem("dataKey");
    const storedSessionKey = sessionStorage.getItem("sessionKey");

    if (storedDataKey && storedSessionKey) {
      this.dataKey = CryptoJS.enc.Base64.parse(storedDataKey);
      this.sessionKey = storedSessionKey;
      return true;
    }
    return false;
  }

  public getKeys(): { sessionKey: string | null; dataKey: string | null } {
    if (!this.sessionKey || !this.dataKey) {
      this.restoreFromStorage();
    }
    return {
      sessionKey: this.sessionKey,
      dataKey: this.dataKey?.toString(CryptoJS.enc.Base64) || null,
    };
  }

  public decryptResponse(encryptedData: string, iv: string): any {
    try {
      if (!this.dataKey) {
        const storedKey = sessionStorage.getItem("dataKey");
        if (!storedKey) {
          throw new Error("No data key available for decryption");
        }
        this.dataKey = CryptoJS.enc.Base64.parse(storedKey);
      }

      const startTime = Date.now();

      // Decrypt the response data with our data key
      const ciphertext = CryptoJS.enc.Base64.parse(encryptedData);
      const ivParsed = CryptoJS.enc.Base64.parse(iv);

      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext } as any,
        this.dataKey,
        {
          iv: ivParsed,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );

      const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedStr) {
        throw new Error("Decryption failed - invalid data");
      }

      const decryptionTime = Date.now() - startTime;
      console.log(`Response decrypted in ${decryptionTime}ms`);

      return JSON.parse(decryptedStr);
    } catch (error) {
      console.error("Decryption error:", error);
      throw new Error(
        `Failed to decrypt response: ${
          error instanceof Error ? error.message : error
        }`
      );
    }
  }

  public clearSession(): void {
    this.dataKey = null;
    this.sessionKey = null;
    this.initPromise = null;
    sessionStorage.removeItem("dataKey");
    sessionStorage.removeItem("sessionKey");
    console.log("Hybrid encryption session cleared");
  }

  public isSessionInitialized(): boolean {
    return (
      (this.dataKey !== null && this.sessionKey !== null) ||
      (sessionStorage.getItem("dataKey") !== null &&
        sessionStorage.getItem("sessionKey") !== null)
    );
  }
}
